import React, { useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import http from '../../httpConfig';
import Toast from '../../components/Toast';
import AuthService from '../../services/auth.service';
import { ERROR_MESSAGES, STATUS_CODE, API_ENDPOINTS } from '../../utils/apiConstant';
import { LOCAL_STORAGE_KEYS } from '../../utils/appConstant';
import { AuthContext } from '../../contexts/AuthContext';
import { Modules, NonAuthRoutes } from '../../enums';

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

interface StoredImpersonationSession {
  impersonationToken: string;
  expiresAt: string;
}

/**
 * Read directly from sessionStorage (the single source of truth ImpersonationProvider
 * writes to) rather than ImpersonationContext — HttpInterceptor mounts in App.tsx
 * above ImpersonationProvider, and an Axios interceptor callback isn't a React render
 * anyway, so there's nothing to gain from routing this through context.
 */
function getActiveImpersonationSession(): StoredImpersonationSession | null {
  const raw = sessionStorage.getItem(LOCAL_STORAGE_KEYS.impersonationSession);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredImpersonationSession;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

const HttpInterceptor: React.FC = () => {
  const navigate = useNavigate();
  const { token, logout, setException, setRoleChanged } = useContext(AuthContext);

  const handleError = useCallback(async (error: any) => {
    // Synthetic rejection from the request interceptor's impersonation write-block below —
    // already toasted there; nothing more to do, and it must not fall into the generic
    // internalServerError branch (which would surface as an unhandled-rejection overlay
    // since this object has no .response for any status check to match).
    if (error?.__impersonationBlocked) {
      return Promise.reject(error);
    }
    if (error?.response?.status === STATUS_CODE.unauthorized) {
      toast.error(
        <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.unauthorized} />,
        { icon: false, toastId: 'unauthorized' }
      );
      await logout(error?.config?.url);
      navigate(Modules.auth + NonAuthRoutes.login);
    } else if (error?.response?.status === STATUS_CODE.notFound) {
      toast.error(
        <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.apiPathNotFound} />,
        { icon: false, toastId: 'not-found' }
      );
      return Promise.reject({
        success: false,
        message: error?.response?.data?.message ?? ERROR_MESSAGES.apiPathNotFound,
        code: error?.response?.data?.code,
      });
    } else if (error?.response?.status === STATUS_CODE.gatewayTimeout) {
      toast.error(<Toast message={ERROR_MESSAGES.gatewayTimeout} />, {
        icon: false,
        toastId: 'gateway-timeout',
      });
      return Promise.reject({ success: false });
    } else if (error?.response?.status === STATUS_CODE.badRequest) {
      toast.error(<Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.badRequest} />, {
        icon: false,
        toastId: 'bad-request',
      });
      return Promise.reject({ success: false, message: ERROR_MESSAGES.badRequest });
    } else if (error?.response?.status === STATUS_CODE.unProcessableEntity) {
      const errors = error?.response?.data?.errors ?? {};
      const messages: any[] = Object.keys(errors)
        .map((errorField) => errors[errorField]?.[0])
        .filter(Boolean);
      toast.error(
        <Toast message={messages.length ? messages : error?.response?.data?.message ?? ERROR_MESSAGES.badRequest} />,
        { icon: false, toastId: 'bad-request' }
      );
      return Promise.reject({ success: false, message: ERROR_MESSAGES.badRequest });
    } else if (error?.response?.status === STATUS_CODE.conflict) {
      toast.error(<Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.badRequest} />, {
        icon: false,
        toastId: 'conflict',
      });
      return Promise.reject({ success: false, message: ERROR_MESSAGES.badRequest });
    } else if (error?.response?.status === STATUS_CODE.forbidden) {
      toast.error(<Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.forbidden} />, {
        icon: false,
        toastId: 'forbidden',
      });
      return Promise.reject({ success: false, message: ERROR_MESSAGES.forbidden });
    }
    else if (error?.response?.status === STATUS_CODE.internalServerError) {
      if (error?.response?.data?.code && error?.response?.data?.code == 1000) {
        setException(error?.response?.data?.data?.correlation_id ?? 'test');
      } else {
        toast.error(
          <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.internalServerError} />,
          { icon: false, toastId: 'unknown' }
        );
      }

      return Promise.reject({ success: false, message: ERROR_MESSAGES.internalServerError });
    } else {
      toast.error(
        <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.internalServerError} />,
        { icon: false, toastId: 'unknown' }
      );
      return Promise.reject({ success: false, message: ERROR_MESSAGES.internalServerError });
    }
  }, []);

  useEffect(() => {
    const reqInterceptor = http.interceptors.request.use(
      (config: any) => {
        const token = AuthService.getToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }

        const impersonationSession = getActiveImpersonationSession();
        if (impersonationSession) {
          config.headers = config.headers || {};
          config.headers['X-Impersonation-Token'] = impersonationSession.impersonationToken;

          // /impersonation/end is itself a write call that must be allowed to go through
          // while impersonating — same exemption the backend applies by router mount order.
          const isEndCall = config.url?.includes(API_ENDPOINTS.impersonationEnd);
          if (!isEndCall && WRITE_METHODS.has((config.method ?? '').toLowerCase())) {
            toast.warn(<Toast message="Write operations are disabled during impersonation" />, {
              icon: false,
              toastId: 'impersonation-write-blocked',
            });
            return Promise.reject({ __impersonationBlocked: true });
          }
        }

        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const resInterceptor = http.interceptors.response.use(
      (response: any) => {
        if (response?.headers?.['x-role-changed'] === 'true') {
          setRoleChanged(true);
        }
        return response?.data ?? {};
      },
      (error: any) => {
        return handleError(error);
      }
    );

    return () => {
      http.interceptors.request.eject(reqInterceptor);
      http.interceptors.response.eject(resInterceptor);
    };
  }, [setRoleChanged]);

  return null;
};

export default HttpInterceptor;
